/**
 * useOfflineSync — React hook for offline status and sync management
 * Monitors connectivity, registers service worker, and triggers sync on reconnect.
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { getAllQueuedRecords } from '@/lib/offlineDB';
import { runSync, SyncResult } from '@/lib/offlineSync';

export interface OfflineSyncState {
  isOnline: boolean;
  isSyncing: boolean;
  pendingCount: number;
  lastSyncResult: SyncResult | null;
  lastSyncAt: Date | null;
  triggerSync: () => Promise<void>;
  refreshPendingCount: () => Promise<void>;
}

export function useOfflineSync(): OfflineSyncState {
  const [isOnline, setIsOnline] = useState(true);
  const [isSyncing, setIsSyncing] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [lastSyncResult, setLastSyncResult] = useState<SyncResult | null>(null);
  const [lastSyncAt, setLastSyncAt] = useState<Date | null>(null);
  const syncLock = useRef(false);

  const refreshPendingCount = useCallback(async () => {
    try {
      const queued = await getAllQueuedRecords();
      setPendingCount(queued.length);
    } catch {
      // IndexedDB not available (SSR)
    }
  }, []);

  const triggerSync = useCallback(async () => {
    if (syncLock.current || !isOnline) return;
    syncLock.current = true;
    setIsSyncing(true);
    try {
      const result = await runSync();
      setLastSyncResult(result);
      setLastSyncAt(new Date());
      await refreshPendingCount();
    } catch (err) {
      console.error('[OfflineSync] Sync failed:', err);
    } finally {
      setIsSyncing(false);
      syncLock.current = false;
    }
  }, [isOnline, refreshPendingCount]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Set initial online state
    setIsOnline(navigator.onLine);

    const handleOnline = () => {
      setIsOnline(true);
      // Auto-trigger sync when coming back online
      setTimeout(() => triggerSync(), 1500);
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for sync requests from service worker
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type === 'SYNC_REQUESTED') {
        triggerSync();
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleMessage);

    // Initial count
    refreshPendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      navigator.serviceWorker?.removeEventListener('message', handleMessage);
    };
  }, [triggerSync, refreshPendingCount]);

  return {
    isOnline,
    isSyncing,
    pendingCount,
    lastSyncResult,
    lastSyncAt,
    triggerSync,
    refreshPendingCount,
  };
}
